import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { css } from '@emotion/react';
import { formatInTimeZone } from 'date-fns-tz';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import { colors } from '@/styles';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { MANIFEST_TYPE_LABELS, getManifestStatusDisplay, INBOUND_MANIFEST_TYPES } from '@/constants/manifestStatuses';
import ExpandableGrid from '@/components/ExpandableGrid';
import { ContextProvider as ManifestsProvider, Context as ManifestsContext } from '@/components/Manifests/store';
import CreateManifest from '@/components/Manifests/modals/CreateManifest';
import { GET_MANIFEST } from '../graphql';
import { SkuCell, NameText, MonoText, DescText, QtyText, PalletText, Pill } from '@/components/ExpandableGrid/blocks';
import { getManifestGridConfig } from './configs';
import { buildGridActions } from './actions';
import {
    summarizeItems,
    sumUnits,
    sumUnitsByStatus,
    totalUnits,
    orderSearchText,
    itemSearchText,
    ORDER_ONLY_SEARCH_NOTE,
    formatQty,
} from './utils';
import { getManifestLineCounts, getManifestShipperNames } from '../utils';
import PickBlockedNotice from './PickBlockedNotice';
import ForwardStockingTag from '@/components/misc/ForwardStockingTag';

const CHILD_FOOTNOTE = (
    <>
        <b>Line ID</b> = unique per piece · <b>Serial</b> = manufacturer per unit · <b>Barcode/QR</b> falls back to an
        internal Onward item ID when a SKU has no serial · <b>Pallet ID</b> groups items (Consolidate assigns one shared
        pallet) · <b>Location</b> = that pallet's warehouse location once stored.
    </>
);

// Thin manifest-specific wrapper around the generic ExpandableGrid: it builds the order-parent
// columns, the header config, and the button configs, and owns the delete-confirm modal. All
// grid mechanics (selection, search, filter, pagination, expansion) live in ExpandableGrid.
const ManifestGridInner = ({
    manifest = {},
    orders = [],
    editable = false,
    callbacks = {},
    pickBlockers = null,
    initialSearchTerm = '',
}) => {
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    // The "+ Add/Remove Orders" header action drives the Create Manifest dialog, which lives in the
    // Manifests store (mounted in modalOnly mode by the wrapper below).
    const { callbacks: manifestsCallbacks } = useContext(ManifestsContext);
    const openUpdateManifest = manifestsCallbacks?.openUpdateManifest;
    const apollo = useApolloClient();

    const config = useMemo(() => getManifestGridConfig(manifest.type), [manifest.type]);
    const actions = useMemo(() => buildGridActions({ manifest, callbacks }), [manifest, callbacks]);
    const statusModel = config.statusModel;
    const isInbound = INBOUND_MANIFEST_TYPES.includes(manifest.type);
    // Status label by category key (Status col value).
    const labelByKey = useMemo(() => Object.fromEntries((config.legend || []).map((c) => [c.key, c.label])), [config]);

    // One summary per order powers every parent-row cell (qty, pallet, chip).
    const summaryByOrder = useMemo(
        () => new Map(orders.map((order) => [order.order_id, summarizeItems(order.manifestItems || [], statusModel)])),
        [orders, statusModel]
    );
    const summaryFor = (order) =>
        summaryByOrder.get(order.order_id) || summarizeItems(order.manifestItems || [], statusModel);

    const parentColumns = useMemo(
        () => [
            {
                key: 'order',
                header: 'Order / PO',
                width: 'minmax(0, 1.6fr)',
                sortable: true,
                filterable: true,
                getValue: (order) => order.order_number || order.po_number || '',
                render: (order, { searchHint } = {}) => (
                    <SkuCell
                        css={css`
                            flex-wrap: wrap;
                            row-gap: 4px;
                        `}
                    >
                        <NameText>{order.order_number || '--'}</NameText>
                        {order.po_number ? <MonoText>{order.po_number}</MonoText> : null}
                        <ForwardStockingTag order={order} />
                        {searchHint === 'order-only' ? <DescText>{ORDER_ONLY_SEARCH_NOTE}</DescText> : null}
                    </SkuCell>
                ),
            },
            {
                key: 'customer',
                header: 'Customer',
                width: '1.2fr',
                sortable: true,
                filterable: true,
                getValue: (order) => order.dropoff_name || '',
                render: (order) => (
                    <SkuCell>
                        <DescText>{order.dropoff_name || '--'}</DescText>
                    </SkuCell>
                ),
            },
            {
                key: 'qty',
                header: 'Qty',
                width: '0.5fr',
                sortable: true,
                getValue: (order) => summaryFor(order).qty,
                render: (order) => <QtyText>{formatQty(summaryFor(order).qty)}</QtyText>,
            },
            {
                key: 'pallet',
                header: 'Pallet ID',
                width: '0.95fr',
                sortable: true,
                filterable: true,
                getValue: (order) => summaryFor(order).pallet,
                render: (order) => {
                    const summary = summaryFor(order);
                    return <PalletText muted={summary.pallet === '—'}>{summary.pallet}</PalletText>;
                },
            },
            {
                key: 'location',
                header: 'Location',
                width: '0.95fr',
                sortable: true,
                filterable: true,
                getValue: (order) => summaryFor(order).location,
                render: (order) => {
                    const summary = summaryFor(order);
                    return <PalletText muted={summary.location === '—'}>{summary.location}</PalletText>;
                },
            },
            {
                key: 'status',
                header: 'Status',
                width: '0.82fr',
                sortable: true,
                filterable: true,
                getValue: (order) => {
                    const chip = summaryFor(order).chip;
                    return labelByKey[chip.key] || chip.label;
                },
                render: (order) => {
                    const summary = summaryFor(order);
                    return <Pill color={summary.chip.color}>{summary.chip.label}</Pill>;
                },
            },
        ],
        // summaryByOrder captures orders + statusModel
        [summaryByOrder, labelByKey]
    );

    const header = useMemo(() => {
        const fields = [
            { label: 'BOL', get: (m) => m.reference_id || m.manifest_number || '--' },
            {
                label: 'Shipper',
                get: (m) => {
                    const names = getManifestShipperNames(m.orders || []);
                    return names.length ? names.join(', ') : '--';
                },
            },
            {
                label: isInbound ? 'Receiving Date' : 'Load Date',
                get: (m) =>
                    m.receiving_date ? formatInTimeZone(new Date(m.receiving_date), 'UTC', 'yyyy-MM-dd') : '--',
            },
            { label: 'Cubic Feet', get: (m) => (m.cubes ? `${Math.round(m.cubes).toLocaleString()} ft³` : '--') },
            {
                label: 'Orders',
                get: (m) => (m.orders || []).length,
            },
            {
                label: 'Item Count',
                get: (m) => {
                    const { lines, units } = getManifestLineCounts(m.orders || []);
                    return `${lines} ${lines === 1 ? 'line' : 'lines'} · ${units} ${units === 1 ? 'unit' : 'units'}`;
                },
            },
            // Route context — shown only for route-linked manifests.
            { label: 'Route', get: (m) => m.route?.route_number || '--', visible: (m) => !!m.route },
            { label: 'Resource', get: (m) => m.route?.resource?.name || '--', visible: (m) => !!m.route?.resource },
            {
                label: 'Scheduled Delivery',
                get: (m) => m.route?.scheduled_delivery_formatted || '--',
                visible: (m) => !!m.route?.scheduled_delivery_formatted,
            },
            ...config.extraHeaderFields(manifest),
        ];

        return {
            title: (m) => m.name || `${MANIFEST_TYPE_LABELS[m.type] || ''} Manifest`.trim(),
            subtitle: () =>
                editable
                    ? null
                    : 'This is an external manifest handled by a partner carrier. Only your orders are shown.',
            statusPill: (m) => {
                const inProgress = sumUnits(m.orders || [], statusModel, config.progress[0].category);
                const total = totalUnits(m.orders || []);
                const status = config.deriveStatus({ manifest: m, inProgress, total });
                return getManifestStatusDisplay(m.type, status) || { label: 'Unknown', color: colors.greys[2] };
            },
            fields,
            // Metadata edit/delete are blocked for route-linked manifests (editing/deleting one tied
            // to an active route can break the route/its staging). Item-level actions stay available.
            actions:
                editable && !manifest.route
                    ? [
                          {
                              key: 'add',
                              icon: AddIcon,
                              color: colors.greens.primary,
                              ariaLabel: 'Add or remove orders',
                              tooltip: 'Add or remove orders',
                              onClick: () => openUpdateManifest?.(manifest),
                          },
                          {
                              key: 'edit',
                              icon: EditIcon,
                              color: colors.greens.primary,
                              ariaLabel: 'Edit manifest',
                              tooltip: 'Edit manifest details',
                              onClick: (m) => callbacks.editManifest(m),
                          },
                          {
                              key: 'delete',
                              icon: DeleteForeverIcon,
                              color: colors.reds.primary,
                              ariaLabel: 'Delete manifest',
                              tooltip: 'Delete manifest',
                              onClick: () => setConfirmingDelete(true),
                          },
                      ]
                    : [],
            progress: config.progress.map((bar) => ({
                label: bar.label,
                verb: bar.verb,
                value: (rows) =>
                    bar.category ? sumUnits(rows, statusModel, bar.category) : sumUnitsByStatus(rows, bar.statuses),
                total: (rows) => totalUnits(rows),
            })),
        };
    }, [config, statusModel, isInbound, editable, manifest, callbacks, openUpdateManifest]);

    const search = useMemo(
        () => ({
            placeholder: 'Search order, PO, SKU, barcode, or serial',
            getOrderText: (order) => orderSearchText(order),
            getChildText: (item) => itemSearchText(item),
            initialTerm: initialSearchTerm,
        }),
        [initialSearchTerm]
    );

    const rowActions = editable ? config.rowButtons(actions, manifest, pickBlockers) : [];
    const parentActions = editable ? config.parentButtons(actions, manifest, pickBlockers) : [];
    const bulkButtons = editable ? config.bulkButtons(actions, manifest, pickBlockers) : [];

    // Stable identities so ExpandableGrid's row/child memos don't recompute every render.
    const getRowId = useCallback((order) => order.order_id, []);
    const getChildren = useCallback((order) => order.manifestItems || [], []);
    const getChildId = useCallback((item) => item.item_id, []);

    return (
        <>
            {editable && !isInbound ? <PickBlockedNotice blockers={pickBlockers} /> : null}

            <ExpandableGrid
                subject={manifest}
                rows={orders}
                getRowId={getRowId}
                getChildren={getChildren}
                getChildId={getChildId}
                parentColumns={parentColumns}
                childColumns={config.childColumns}
                header={header}
                legend={config.legend}
                search={search}
                bulkButtons={bulkButtons}
                parentActions={parentActions}
                rowActions={rowActions}
                selectable={editable}
                selectionNoun="item"
                childPanelLabel="Unit identity"
                childFootnote={CHILD_FOOTNOTE}
                columnOps
                resizable
                emptyText="No orders on this manifest."
                emptyFilteredText="No items match your search or filters."
            />

            <NavResponsiveModal open={confirmingDelete} onClose={() => setConfirmingDelete(false)}>
                <ModalHeader title="Delete Manifest" onClose={() => setConfirmingDelete(false)} />
                <ModalContent
                    css={css`
                        width: 460px;
                    `}
                >
                    Are you sure you want to delete <b>{manifest.name || `Manifest ${manifest.manifest_number}`}</b>?
                    This cannot be undone.
                </ModalContent>
                <ModalActions>
                    <SecondaryButton onClick={() => setConfirmingDelete(false)}>Cancel</SecondaryButton>
                    <PrimaryButton
                        red
                        onClick={() => {
                            setConfirmingDelete(false);
                            callbacks.deleteManifest(manifest);
                        }}
                    >
                        Delete
                    </PrimaryButton>
                </ModalActions>
            </NavResponsiveModal>

            {/* Add/Remove Orders dialog (update mode) — refresh the detail manifest on save. */}
            <CreateManifest onUpdated={() => apollo.refetchQueries({ include: [GET_MANIFEST] })} />
        </>
    );
};

// Mount the Manifests store in modalOnly mode so the "+" header action can drive the Create Manifest
// dialog in-place without pulling in the whole Manifests list page.
const ManifestGrid = (props) => (
    <ManifestsProvider modalOnly>
        <ManifestGridInner {...props} />
    </ManifestsProvider>
);

export default ManifestGrid;
